/**
 * A month or a week of days, and nothing about what sits on them.
 *
 * The grid arithmetic — Monday first, the days either side of the month, which
 * cell is today, how "Prev" behaves at a year boundary — is the same wherever a
 * calendar appears, and it is the part that is quietly easy to get wrong. What
 * differs per screen is the chip inside a day, so that is a render prop and the
 * rest is here.
 *
 * ## Local dates, deliberately
 *
 * Every date in and out of this component is a local `YYYY-MM-DD` string, built
 * from local components. Nothing here goes near `toISOString()`: Norway is an
 * hour or two ahead of UTC, so local midnight is the previous day in
 * Greenwich — which is how a calendar comes to draw a post on the wrong day and
 * agree with itself while doing it. See `plusDaysIso` in `util/dates` for the
 * same mistake made in a document, and what it cost.
 */
import type { ReactNode } from 'react'
import './CalendarGrid.css'

export type CalendarSpan = 'month' | 'week'

export interface CalendarCell {
  /** The day, as a local ISO date. */
  key: string
  day: number
  /** `Mon`. Carried on the cell because the week span has no header strip. */
  weekday: string
  /** False for the days either side of the month. Always true in a week. */
  inMonth: boolean
  isToday: boolean
}

interface Props {
  span: CalendarSpan
  /** Omit to lock the calendar to one span — no switch is rendered. */
  onSpanChange?: (span: CalendarSpan) => void
  /** Any date inside the period being shown. */
  anchor: string
  onAnchorChange: (iso: string) => void
  /** Today, passed in rather than read here, so one clock decides. */
  today: string
  /** What sits in a day. */
  children: (cell: CalendarCell) => ReactNode
  /** Beside the period label — a legend, a count. */
  actions?: ReactNode
  /** Under the grid — undated records, a note. */
  footer?: ReactNode
}

/** Monday-first, which is how a Norwegian week reads. */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const isoOf = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const parse = (iso: string) => new Date(`${iso}T00:00:00`)

/** Monday of the week `iso` falls in. */
function weekStart(iso: string): Date {
  const d = parse(iso)
  // JavaScript's Sunday is 0, so shift it to the end of the week.
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return d
}

function buildCells(span: CalendarSpan, anchor: string, today: string): CalendarCell[] {
  const a = parse(anchor)
  const month = a.getMonth()
  const start =
    span === 'week' ? weekStart(anchor) : weekStart(isoOf(new Date(a.getFullYear(), month, 1)))
  // Six weeks always, so the grid does not resize as months change under it.
  const count = span === 'week' ? 7 : 42
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    const key = isoOf(d)
    return {
      key,
      day: d.getDate(),
      weekday: WEEKDAYS[(d.getDay() + 6) % 7]!,
      inMonth: span === 'week' || d.getMonth() === month,
      isToday: key === today,
    }
  })
}

/** "September 2026", or "28 Sep – 4 Oct 2026" when a week crosses a month. */
function periodLabel(span: CalendarSpan, cells: CalendarCell[], anchor: string): string {
  if (span === 'month') {
    return parse(anchor).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  }
  const from = parse(cells[0]!.key)
  const to = parse(cells[cells.length - 1]!.key)
  const sameMonth = from.getMonth() === to.getMonth()
  const left = from.toLocaleDateString('en-GB', sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' })
  const right = to.toLocaleDateString('en-GB', { day: 'numeric', month: sameMonth ? 'long' : 'short', year: 'numeric' })
  return `${left} – ${right}`
}

export function CalendarGrid({
  span,
  onSpanChange,
  anchor,
  onAnchorChange,
  today,
  children,
  actions,
  footer,
}: Props) {
  const cells = buildCells(span, anchor, today)

  const shift = (by: number) => {
    if (span === 'week') {
      const d = weekStart(anchor)
      d.setDate(d.getDate() + by * 7)
      onAnchorChange(isoOf(d))
      return
    }
    // The 1st, not the current day: stepping from the 31st lands on a month
    // that has no 31st and JavaScript rolls it into the next one.
    const a = parse(anchor)
    onAnchorChange(isoOf(new Date(a.getFullYear(), a.getMonth() + by, 1)))
  }

  return (
    <div className={`calgrid calgrid--${span}`}>
      <div className="calgrid__head">
        <div className="calgrid__period">{periodLabel(span, cells, anchor)}</div>
        <div className="calgrid__nav">
          {actions}
          <button className="chip" onClick={() => shift(-1)} aria-label={`Previous ${span}`}>
            ‹ Prev
          </button>
          <button className="chip" onClick={() => onAnchorChange(today)}>
            Today
          </button>
          <button className="chip" onClick={() => shift(1)} aria-label={`Next ${span}`}>
            Next ›
          </button>
          {onSpanChange && (
            <div className="calgrid__span" role="group" aria-label="Calendar span">
              <button
                className="chip"
                aria-pressed={span === 'month'}
                onClick={() => onSpanChange('month')}
              >
                Month
              </button>
              <button
                className="chip"
                aria-pressed={span === 'week'}
                onClick={() => onSpanChange('week')}
              >
                Week
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="calgrid__weekdays">
        {WEEKDAYS.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>

      <div className="calgrid__grid">
        {cells.map((c) => (
          <div
            key={c.key}
            className={`calgrid__cell${c.inMonth ? '' : ' calgrid__cell--out'}${
              c.isToday ? ' calgrid__cell--today' : ''
            }`}
          >
            {/* On a phone the grid becomes a list of the days that have
                something on them, so the outlined cell that marks today on
                desktop is not visible — the weekday and the word are. */}
            <div className="calgrid__day">
              <span className="calgrid__weekday">{c.weekday}</span> {c.day}
              {c.isToday && <span className="calgrid__todaymark">Today</span>}
            </div>
            {children(c)}
          </div>
        ))}
      </div>

      {footer}
    </div>
  )
}
